import { Services, WedgeProduct } from '@ekhodealer/ekho-common';

import type { NavigationItemId } from '@config/navigation/navigationItems';
import type { SettingsSection } from '@config/paths/settingsSections';
import type { OnboardingField } from '@features/product-onboarding/constants/onboardingFields';

/**
 * Defines a product module that can be gated behind a subscription.
 * Add new entries here as more product pages are added.
 */
export interface ProductModuleConfig {
	/** The Services enum key used to check config[service].supported */
	service: Services;
	/** Display name shown in the upgrade overlay and badges */
	displayName: string;
	/** Compelling description shown in the upgrade overlay */
	description: string;
	/** Label for the subscribe CTA */
	subscribeCta: string;
	/** Navigation item IDs that should show a badge + frosted overlay when locked */
	navItems: NavigationItemId[];
	/** Settings sections that should show a badge + frosted overlay when locked */
	settingsSections: SettingsSection[];
	/** Product catalog ID from the onboarding flow (e.g. 'ai-bdc') — used to route users after onboarding */
	onboardingProductId?: string;
	/** Path within the docs site (e.g. '/dealer-docs/admin-portal/ai-bdc/overview') — used by useDocsAuth to open with JWT */
	docsPath?: string;
	/** Fields required from the dealer during onboarding for this product module */
	requiredFields?: OnboardingField[];
	/** Loom share URL for the promotional video shown in the upgrade overlay */
	promoVideoUrl?: string;
}

// TODO: Replace with real HubSpot sales calendar link
export const SALES_CALENDAR_URL = 'https://meetings.hubspot.com/ekho';

/**
 * Registry of all product modules that can be subscription-gated.
 *
 * To add a new gated product:
 *   1. Add an entry below with its Services key, copy, nav items, and settings sections
 *   2. The sidebar badge, page overlay, and settings overlay will work automatically
 */
export const PRODUCT_MODULES = {
	transactionLayer: {
		service: Services.PROGRAM_OWNER,
		displayName: 'Transaction Layer',
		description:
			'Enable online checkout, financing, titling, and the full transaction experience for your dealership website.',
		subscribeCta: 'Get started',
		navItems: [],
		settingsSections: ['products/website-page'],
	},
	aiBdc: {
		service: Services.BDC,
		displayName: 'AI BDC',
		description:
			'Put an AI-powered sales assistant to work around the clock — engaging every lead instantly across SMS and web chat. It qualifies prospects, books appointments, and follows up automatically, so your team can focus on closing deals instead of chasing them.',
		subscribeCta: 'Get started',
		navItems: ['conversations'],
		settingsSections: ['products/ai-bdc'],
		onboardingProductId: WedgeProduct.AI_BDC,
		docsPath: '/dealer-docs/admin-portal/ai-bdc/overview',
		promoVideoUrl: 'https://www.loom.com/share/0c8bacdd0b6c4e1a97a484a7c612cab5',
	},
} as const satisfies Record<string, ProductModuleConfig>;

export type ProductModuleId = keyof typeof PRODUCT_MODULES;
